const BRINK_VENDOR = 'brink';
const IGNORED_PLU_TYPES = new Set(['ignore', 'empty']);

type BrinkPosIdPlu = {
  pluType?: string | null;
  constantPlu?: string | null;
};

type IncentiveWithBrinkPosIds = {
  vendorConfigs?: unknown;
  posIdsConfiguration?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isBrinkVendor = (vendor: string | null | undefined): vendor is string =>
  !!vendor && vendor.toLowerCase() === BRINK_VENDOR;

const readVendorConfigs = (vendorConfigs: unknown): Record<string, unknown> =>
  isRecord(vendorConfigs) ? vendorConfigs : {};

const readBrinkPlu = (posIdsConfiguration: unknown): BrinkPosIdPlu | undefined => {
  if (!isRecord(posIdsConfiguration) || !isRecord(posIdsConfiguration.plu)) {
    return undefined;
  }

  const { plu } = posIdsConfiguration;
  return {
    ...plu,
    pluType: typeof plu.pluType === 'string' ? plu.pluType : null,
    constantPlu: typeof plu.constantPlu === 'string' ? plu.constantPlu : null,
  };
};

const hasUsableBrinkPlu = (plu: BrinkPosIdPlu | undefined): plu is BrinkPosIdPlu => {
  if (!plu?.pluType || IGNORED_PLU_TYPES.has(plu.pluType)) {
    return false;
  }

  if (plu.pluType === 'constantPlu') {
    return Boolean(plu.constantPlu);
  }

  return true;
};

/**
 * Brink promo PLUs are stored on posIdsConfiguration, not vendorConfigs.brink.
 * Copy that PLU onto the current Brink vendor key so availability uses the same
 * price check as an NCR vendorConfigs entry.
 */
export const withBrinkPosIdVendorConfig = <T extends IncentiveWithBrinkPosIds>(
  incentive: T,
  vendor: string | null
): T => {
  if (!isBrinkVendor(vendor)) {
    return incentive;
  }

  const vendorConfigs = readVendorConfigs(incentive.vendorConfigs);
  const existingBrinkKey = Object.keys(vendorConfigs).find(
    key => key.toLowerCase() === BRINK_VENDOR
  );
  if (existingBrinkKey && vendorConfigs[existingBrinkKey] != null) {
    return incentive;
  }

  const brinkPlu = readBrinkPlu(incentive.posIdsConfiguration);
  if (!hasUsableBrinkPlu(brinkPlu)) {
    return incentive;
  }

  return {
    ...incentive,
    vendorConfigs: {
      ...vendorConfigs,
      [vendor]: brinkPlu,
    },
  };
};
